import React, { FunctionComponent, forwardRef } from 'react';
import styled from 'styled-components';
import { ItemProps, StyledItemProps } from './ItemTypes';
import {
  ColsType,
  PositionMobileType,
  PositionType,
  ColsDashboardType,
} from './types';
import { CuiTheme } from '../types';
import { screens } from '../styles/breakpoints';
import {
  MOBILE_COLS,
  DESKTOP_COLS,
  DASHBOARD_COLS,
  getFlex,
  getGrid,
  getItemsPerRowFlex,
  getItemsPerRowGrid,
  getColsForPosition,
  getColsNum,
} from './helpers';

const getContainerFlex = (
  theme: CuiTheme,
  cols?: ColsType<ColsDashboardType, ColsDashboardType>,
) => {
  if (!cols && cols !== 0) {
    return '';
  }

  let styles = `
    /** flex fallback for older browsers */
    display: ${getColsNum('xs', cols) > 0 ? 'flex' : 'none'};
    flex-flow: wrap;
  `;

  Object.keys(screens).forEach((k, i) => {
    const key = k as 'sm' | 'md' | 'lg';
    const colsNum = getColsNum(key, cols);

    if (
      colsNum >= 0 &&
      (i === 0
        ? colsNum !== getColsNum('xs', cols)
        : colsNum !==
          getColsNum(Object.keys(screens)[i - 1] as 'sm' | 'md' | 'lg', cols))
    ) {
      styles = styles.concat(`
        ${theme.breakpoints.up(key)} {
          display: ${colsNum > 0 ? 'flex' : 'none'};
        }
      `);
    }
  });

  return styles;
};

const getContainerGrid = (
  theme: CuiTheme,
  containerCols: ColsType<ColsDashboardType, ColsDashboardType>,
  colsProp?: ColsType<ColsDashboardType, ColsDashboardType>,
  position?: PositionMobileType | PositionType,
) => {
  if (!colsProp && !position) {
    return '';
  }

  let cols = colsProp;

  if (!cols) {
    const contCols = {
      xs: getColsNum('xs', containerCols),
      sm: getColsNum('sm', containerCols),
      md: getColsNum('md', containerCols),
      lg: getColsNum('lg', containerCols),
    };
    const contColsXs = contCols.xs;
    const contColsSm = contCols.sm >= 0 ? contCols.sm : contColsXs;
    const contColsMd = contCols.md >= 0 ? contCols.md : contColsSm;
    const contColsLg = contCols.lg >= 0 ? contCols.lg : contColsMd;

    // @ts-ignore
    cols = {
      xs:
        getColsForPosition(
          contColsXs,
          typeof position === 'string' ? position : position!.xs,
        ) || 0,
      sm:
        getColsForPosition(
          contColsSm,
          typeof position === 'string' ? position : position!.sm,
          true,
        ) || 0,
      md: getColsForPosition(
        contColsMd,
        typeof position === 'string' ? position : position!.md,
        true,
      ),
      lg: getColsForPosition(
        contColsLg,
        typeof position === 'string' ? position : position!.lg,
        true,
      ),
    };
  }

  let styles = `
    display: ${getColsNum('xs', cols) > 0 ? '-ms-grid' : 'none'};
    display: ${getColsNum('xs', cols) > 0 ? 'grid' : 'none'};
    -ms-grid-columns: repeat(${getColsNum('xs', cols)}, 1fr);
    grid-template-columns: repeat(${getColsNum('xs', cols)}, 1fr);
  `;

  Object.keys(screens).forEach((k, i) => {
    const key = k as 'sm' | 'md' | 'lg';
    const colsNum = getColsNum(key, cols);

    if (
      colsNum >= 0 &&
      (i === 0
        ? colsNum !== getColsNum('xs', cols)
        : colsNum !==
          getColsNum(Object.keys(screens)[i - 1] as 'sm' | 'md' | 'lg', cols))
    ) {
      styles = styles.concat(`
        ${theme.breakpoints.up(key)} {
          display: ${colsNum > 0 ? '-ms-grid' : 'none'};
          display: ${colsNum > 0 ? 'grid' : 'none'};
          -ms-grid-columns: repeat(${colsNum}, 1fr);
          grid-template-columns: repeat(${colsNum}, 1fr);
        }
      `);
    }
  });

  return styles;
};

const StyledItem = styled.div<StyledItemProps>`
  ${({ isSubgrid, theme, cols }) =>
    isSubgrid ? getContainerFlex(theme, cols) : ''}
  ${({
    theme,
    position,
    posOffset,
    containerCols,
    cols,
    offset,
    customFallback,
  }) => {
    if (customFallback) {
      return customFallback;
    }
    let flexboxFallback = '';
    const pos = position
      ? typeof position === 'string'
        ? position
        : position.xs
      : undefined;
    const posOff = posOffset
      ? typeof posOffset === 'string'
        ? posOffset
        : posOffset.xs
      : undefined;

    if (pos) {
      flexboxFallback = flexboxFallback.concat(
        getItemsPerRowFlex(getColsNum('xs', containerCols), pos, posOff),
      );
    } else if (cols) {
      flexboxFallback = flexboxFallback.concat(
        getFlex(
          getColsNum('xs', containerCols),
          getColsNum('xs', cols),
          offset ? getColsNum('xs', offset) : 0,
        ),
      );
    }

    Object.keys(screens).forEach(k => {
      const key = k as 'xs' | 'sm' | 'md' | 'lg';
      const bpPos = position
        ? key === 'sm' && typeof position === 'string'
          ? position
          : position[key]
        : undefined;
      const bpPosOff = posOffset
        ? key === 'sm' && typeof posOffset === 'string'
          ? posOffset
          : posOffset[key]
        : undefined;

      if (bpPos) {
        flexboxFallback = flexboxFallback.concat(`
          ${theme.breakpoints.up(key)} {
            ${getItemsPerRowFlex(
              getColsNum(key, containerCols),
              bpPos,
              bpPosOff,
              true,
            )}
          }
        `);
      } else if (cols && getColsNum(key, cols) >= 0) {
        flexboxFallback = flexboxFallback.concat(`
          ${theme.breakpoints.up(key)} {
            ${getFlex(
              getColsNum(key, containerCols),
              getColsNum(key, cols),
              offset ? getColsNum(key, offset) : 0,
            )}
          }
        `);
      }
    });

    return flexboxFallback;
  }}
`;

const StyledGridItem = styled(StyledItem)`
  @supports (display: grid) {
    width: auto;
    max-width: none;
    margin-left: 0;
    margin-right: 0;
    ${({ theme, containerCols, cols, position, isSubgrid }) =>
      isSubgrid ? getContainerGrid(theme, containerCols!, cols, position) : ''}
    ${({ theme, position, containerCols, cols, startAt }) => {
      let gridStyles = '';
      const pos = position
        ? typeof position === 'string'
          ? position
          : position.xs
        : undefined;

      if (pos) {
        gridStyles = gridStyles.concat(
          getItemsPerRowGrid(getColsNum('xs', containerCols), pos),
        );
      } else if (cols) {
        const startAtCols = startAt
          ? getColsNum('xs', startAt as ColsType<
              ColsDashboardType,
              ColsDashboardType
            >)
          : 'auto';

        gridStyles = gridStyles.concat(
          getGrid(
            getColsNum('xs', cols),
            startAtCols > 0 ? startAtCols : 'auto',
          ),
        );
      }

      Object.keys(screens).forEach(k => {
        const key = k as 'xs' | 'sm' | 'md' | 'lg';
        const bpPos = position
          ? key === 'sm' && typeof position === 'string'
            ? position
            : position[key]
          : undefined;

        if (bpPos) {
          gridStyles = gridStyles.concat(`
            ${theme.breakpoints.up(key)} {
              ${getItemsPerRowGrid(
                containerCols![key]
                  ? containerCols![key]
                  : getColsNum('sm', containerCols),
                bpPos,
                true,
              )}
            }
          `);
        } else if (cols && getColsNum(key, cols) >= 0) {
          const startAtCols = startAt
            ? getColsNum(key, startAt as ColsType<
                ColsDashboardType,
                ColsDashboardType
              >)
            : 'auto';

          gridStyles = gridStyles.concat(`
            ${theme.breakpoints.up(key)} {
              ${getGrid(
                getColsNum(key, cols),
                startAtCols > 0 ? startAtCols : 'auto',
              )}
            }
          `);
        }
      });

      return gridStyles;
    }}
  }
`;

const Item: FunctionComponent<ItemProps> = forwardRef<
  HTMLDivElement,
  ItemProps
>(
  (
    {
      children,
      onlyFlex,
      containerCols,
      type = 'default',
      as = 'div',
      ...other
    },
    ref,
  ) => {
    const cols =
      type === 'dashboard'
        ? DASHBOARD_COLS
        : {
            xs: MOBILE_COLS,
            sm: DESKTOP_COLS,
          };
    const props = {
      as,
      containerCols: containerCols || cols,
      ref,
      ...other,
    };

    if (onlyFlex) {
      return (
        <StyledItem onlyFlex {...props}>
          {children}
        </StyledItem>
      );
    }
    return <StyledGridItem {...props}>{children}</StyledGridItem>;
  },
);

export default Item;
